import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './AppIcon';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  SEVERITY,
} from '../utils/notificationStore';

const SEVERITY_CONFIG = {
  [SEVERITY?.CRITICAL]: {
    color: '#FF5722',
    bg: 'rgba(255,87,34,0.1)',
    border: 'rgba(255,87,34,0.3)',
    label: 'Kritisk',
    icon: 'AlertTriangle',
  },
  [SEVERITY?.WARNING]: {
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.1)',
    border: 'rgba(245,158,11,0.3)',
    label: 'Advarsel',
    icon: 'AlertCircle',
  },
};

const formatRelativeTime = (iso) => {
  try {
    const diff = Date.now() - new Date(iso)?.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Akkurat nå';
    if (mins < 60) return `${mins} min siden`;
    if (hours < 24) return `${hours}t siden`;
    return `${days}d siden`;
  } catch {
    return '';
  }
};

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  const refresh = useCallback(() => {
    setNotifications(getNotifications());
    setUnreadCount(getUnreadCount());
  }, []);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window?.addEventListener('veiassistent_notification', handler);
    const interval = setInterval(refresh, 5000);
    return () => {
      window?.removeEventListener('veiassistent_notification', handler);
      clearInterval(interval);
    };
  }, [refresh]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => setIsOpen(prev => !prev);

  const handleMarkRead = (id) => {
    markAsRead(id);
    refresh();
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
    refresh();
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={handleToggle}
        aria-label={`Varsler${unreadCount > 0 ? ` — ${unreadCount} uleste` : ''}`}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '38px',
          height: '38px',
          background: isOpen ? 'rgba(255,87,34,0.08)' : 'transparent',
          border: isOpen ? '1.5px solid rgba(255,87,34,0.3)' : '1.5px solid transparent',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          transition: 'all 200ms ease-out',
          color: isOpen ? '#FF5722' : 'var(--color-muted-foreground)',
        }}
        onMouseEnter={e => {
          if (!isOpen) {
            e.currentTarget.style.background = 'rgba(255,87,34,0.06)';
            e.currentTarget.style.color = '#FF5722';
          }
        }}
        onMouseLeave={e => {
          if (!isOpen) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--color-muted-foreground)';
          }
        }}
      >
        <Icon name="Bell" size={18} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              minWidth: '16px',
              height: '16px',
              background: '#FF5722',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-heading)',
              fontSize: '10px',
              fontWeight: 700,
              color: '#fff',
              padding: '0 3px',
              lineHeight: 1,
              boxShadow: '0 0 0 2px var(--color-background)',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            width: '360px',
            maxWidth: 'calc(100vw - 24px)',
            background: 'var(--color-surface)',
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
            zIndex: 9999,
            overflow: 'hidden',
            animation: 'notifSlideDown 180ms cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div style={{ position: 'absolute', top: 0, right: 0, width: '40px', height: '40px', borderBottom: '1.5px solid rgba(255,87,34,0.15)', borderLeft: '1.5px solid rgba(255,87,34,0.15)', pointerEvents: 'none' }} />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 18px 14px',
              borderBottom: '1.5px solid var(--color-border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name="Bell" size={16} color="#FF5722" />
              <span
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: 'var(--color-foreground)',
                }}
              >
                Varsler
              </span>
              {unreadCount > 0 && (
                <span
                  style={{
                    background: 'rgba(255,87,34,0.12)',
                    border: '1px solid rgba(255,87,34,0.3)',
                    borderRadius: '10px',
                    padding: '1px 7px',
                    fontFamily: 'var(--font-heading)',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#FF5722',
                  }}
                >
                  {unreadCount} uleste
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-caption)',
                  fontSize: '12px',
                  color: 'var(--color-primary)',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  transition: 'background 150ms ease',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,79,152,0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                Merk alle lest
              </button>
            )}
          </div>

          <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
            {notifications?.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 24px',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'rgba(0,79,152,0.06)',
                    border: '1.5px solid rgba(0,79,152,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name="BellOff" size={20} color="var(--color-muted-foreground)" />
                </div>
                <p
                  style={{
                    fontFamily: 'var(--font-caption)',
                    fontSize: '13px',
                    color: 'var(--color-muted-foreground)',
                    textAlign: 'center',
                  }}
                >
                  Ingen varsler ennå
                </p>
              </div>
            ) : (
              notifications?.map((notif) => {
                const cfg = SEVERITY_CONFIG?.[notif?.severity] || SEVERITY_CONFIG?.[SEVERITY?.WARNING];
                return (
                  <div
                    key={notif?.id}
                    onClick={() => handleMarkRead(notif?.id)}
                    style={{
                      display: 'flex',
                      gap: '12px',
                      padding: '14px 18px',
                      borderBottom: '1px solid var(--color-border)',
                      background: notif?.read ? 'transparent' : 'rgba(255,87,34,0.03)',
                      cursor: 'pointer',
                      transition: 'background 150ms ease',
                      position: 'relative',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,79,152,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = notif?.read ? 'transparent' : 'rgba(255,87,34,0.03)'}
                  >
                    {!notif?.read && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '6px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '5px',
                          height: '5px',
                          borderRadius: '50%',
                          background: cfg?.color,
                        }}
                      />
                    )}
                    <div
                      style={{
                        flexShrink: 0,
                        width: '34px',
                        height: '34px',
                        borderRadius: 'var(--radius-sm)',
                        background: cfg?.bg,
                        border: `1.5px solid ${cfg?.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '1px',
                      }}
                    >
                      <Icon name={cfg?.icon} size={16} color={cfg?.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '3px' }}>
                        <span
                          style={{
                            fontFamily: 'var(--font-heading)',
                            fontSize: '13px',
                            fontWeight: notif?.read ? 500 : 700,
                            color: 'var(--color-foreground)',
                            lineHeight: 1.3,
                          }}
                        >
                          {notif?.title}
                        </span>
                        <span
                          style={{
                            flexShrink: 0,
                            fontFamily: 'var(--font-caption)',
                            fontSize: '11px',
                            color: 'var(--color-muted-foreground)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatRelativeTime(notif?.timestamp)}
                        </span>
                      </div>
                      <p
                        style={{
                          fontFamily: 'var(--font-caption)',
                          fontSize: '12px',
                          color: 'var(--color-muted-foreground)',
                          lineHeight: 1.5,
                          margin: 0,
                        }}
                      >
                        {notif?.message}
                      </p>
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: '6px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          background: cfg?.bg,
                          border: `1px solid ${cfg?.border}`,
                          fontFamily: 'var(--font-heading)',
                          fontSize: '10px',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: cfg?.color,
                        }}
                      >
                        {cfg?.label}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {notifications?.length > 0 && (
            <div
              style={{
                padding: '10px 18px',
                borderTop: '1.5px solid var(--color-border)',
                background: 'rgba(0,79,152,0.02)',
              }}
            >
              <p
                style={{
                  fontFamily: 'var(--font-caption)',
                  fontSize: '11px',
                  color: 'var(--color-muted-foreground)',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                {notifications?.length} varsler totalt · Klikk for å markere som lest
              </p>
            </div>
          )}
        </div>
      )}
      <style>{`
        @keyframes notifSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default NotificationCenter;
